import { useQuery } from "convex/react";
import { api } from "@convex-api";
import type { GenericId } from "convex/values";

export type PLCheckStatus =
  | { status: "loading" }
  | {
      status: "authorized";
      staffId: GenericId<"staff">;
      /**
       * `true` for a `learnAccess: "read"` viewer — authorized to see every
       * cohort, holding no write role. Drives which controls render; the server
       * enforces the actual restriction on each mutation.
       */
      readOnly: boolean;
      /**
       * `true` for `mcpAccess: "superadmin"` (Lisa, Tara). Gates the
       * "view as read-only" preview toggle only — it grants nothing.
       */
      superadmin: boolean;
    }
  | { status: "forbidden" };

export function useProgramLeaderCheck(): PLCheckStatus {
  const result = useQuery(api.auth.whoami);
  if (result === undefined) return { status: "loading" };
  // Treat missing staffId on an authorized response as forbidden — the
  // backend contract guarantees staffId when authorized, but if the shape
  // ever drifts we'd rather deny than silently propagate undefined.
  if (!result.authorized || !result.staffId) return { status: "forbidden" };
  // `readOnly` absent → treat as writable. A PL response omits it only if the
  // backend contract drifts, and the write mutations are gated server-side
  // regardless, so defaulting to full access here cannot grant anything.
  return {
    status: "authorized",
    staffId: result.staffId,
    readOnly: result.readOnly === true,
    superadmin: result.superadmin === true,
  };
}
